import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SimplePdfAnnotator from './SimplePdfAnnotator';
import MarkupExportDialog from './MarkupExportDialog';
import { documentMarkupApi } from '../utils/documentMarkupApi';
import { base64ToBytes, loadMarkupSource } from './markupSource';
import { convertLegacyStrokes, isLegacyStroke, legacyWorkspaceSize } from './markupGeometry';
import './DocumentMarkupEditor.css';

// Pen, highlighter and eraser each remember their own settings
const DEFAULT_TOOL_SETTINGS = {
    pen: { color: '#000000', thickness: 2, opacity: 1 },
    highlighter: { color: '#ffe600', thickness: 14, opacity: 0.35 },
    eraser: { color: '#000000', thickness: 10, opacity: 1 }
};

const LEGACY_NOTICE_STORAGE_KEY = 'bb-markup-legacy-notice';

// Documents whose "older annotations were repositioned" notice was already shown
const readLegacyNoticeIds = () => {
    try {
        const ids = JSON.parse(window.localStorage.getItem(LEGACY_NOTICE_STORAGE_KEY) || '[]');
        return Array.isArray(ids) ? ids : [];
    } catch (err) {
        return [];
    }
};

const rememberLegacyNotice = (documentId) => {
    try {
        const ids = readLegacyNoticeIds().filter(id => id !== documentId);
        window.localStorage.setItem(LEGACY_NOTICE_STORAGE_KEY, JSON.stringify([...ids, documentId].slice(-200)));
    } catch (err) {
        // Private browsing or blocked storage: the notice may show again next time.
    }
};

function SimpleDocumentMarkupEditor() {
    const { documentId } = useParams();
    const navigate = useNavigate();
    
    // Document state
    const [documentData, setDocumentData] = useState(null);
    const [source, setSource] = useState(null); // The opened PDF or image (see markupSource.js)
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    
    // Strokes state (per page)
    const [pageStrokes, setPageStrokes] = useState({}); // { pageNum: [strokes] }
    
    // UI state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [currentTool, setCurrentTool] = useState('pen');
    const [toolSettingsByTool, setToolSettingsByTool] = useState(DEFAULT_TOOL_SETTINGS);
    const [legacyNotice, setLegacyNotice] = useState(false);
    const [showExport, setShowExport] = useState(false);
    
    const saveTimeoutRef = useRef(null);
    const saveQueueRef = useRef(Promise.resolve());
    const fileBytesRef = useRef(null);
    const workspaceRef = useRef(null);
    const legacyWorkspaceRef = useRef(null);

    const toolSettings = toolSettingsByTool[currentTool] || DEFAULT_TOOL_SETTINGS.pen;
    const updateToolSettings = (changes) => {
        setToolSettingsByTool(prev => ({
            ...prev,
            [currentTool]: { ...prev[currentTool], ...changes }
        }));
    };

    // Load document on mount
    useEffect(() => {
        loadDocument();
    }, [documentId]);

    // Release the opened file when it is replaced or the editor closes
    useEffect(() => {
        return () => {
            if (source) source.destroy();
        };
    }, [source]);

    // Load strokes for current page. This waits for the document to be on
    // screen: converting legacy strokes needs the size of the workspace.
    useEffect(() => {
        if (source && !loading && !pageStrokes[currentPage]) {
            loadPageStrokes(currentPage);
        }
    }, [currentPage, source, loading]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === 'ArrowLeft' && currentPage > 1) {
                goToPage(currentPage - 1);
            } else if (e.key === 'ArrowRight' && currentPage < totalPages) {
                goToPage(currentPage + 1);
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [currentPage, totalPages]);

    const loadDocument = async () => {
        try {
            setLoading(true);
            setError('');
            
            const docData = await documentMarkupApi.getDocument(documentId);
            
            // Open the file once; the annotator and the export share it
            const fileBytes = base64ToBytes(docData.file_data);
            const openedSource = await loadMarkupSource(fileBytes, docData.file_type);
            fileBytesRef.current = fileBytes;

            setDocumentData(docData);
            setSource(openedSource);
            setTotalPages(openedSource.numPages);
            setCurrentPage(1);
            setPageStrokes({});
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadPageStrokes = async (pageNum) => {
        try {
            const data = await documentMarkupApi.getStrokes(documentId, pageNum);
            let strokes = data.strokes || [];

            // Strokes saved before BBD-125 hold on-screen pixels. Convert them
            // to page space once and save them back (see markupGeometry.js).
            if (strokes.some(isLegacyStroke)) {
                const pageSize = await source.getPageSize(pageNum);
                // Measured once, before the notice below takes space from the workspace
                if (!legacyWorkspaceRef.current) {
                    const workspace = workspaceRef.current;
                    legacyWorkspaceRef.current = legacyWorkspaceSize(
                        workspace ? { width: workspace.clientWidth, height: workspace.clientHeight } : null
                    );
                }
                const result = convertLegacyStrokes(strokes, pageSize, legacyWorkspaceRef.current);
                strokes = result.strokes;
                console.info(`Document markup ${documentId}: converted ${result.converted} legacy stroke(s) on page ${pageNum} to page coordinates`);
                savePageStrokes(pageNum, strokes);

                if (!readLegacyNoticeIds().includes(documentId)) {
                    rememberLegacyNotice(documentId);
                    setLegacyNotice(true);
                }
            }

            setPageStrokes(prev => ({
                ...prev,
                [pageNum]: strokes
            }));
        } catch (err) {
            console.error(`Failed to load strokes for page ${pageNum}:`, err);
            setPageStrokes(prev => ({
                ...prev,
                [pageNum]: []
            }));
        }
    };

    const handleStrokeAdded = (stroke) => {
        const updatedStrokes = [...(pageStrokes[currentPage] || []), stroke];
        
        // Update state immediately
        setPageStrokes(prev => ({
            ...prev,
            [currentPage]: updatedStrokes
        }));
        
        // Auto-save after 2 seconds
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            savePageStrokes(currentPage, updatedStrokes);
        }, 2000);
    };

    const handleStrokesChanged = (updatedStrokes) => {
        // Update state immediately
        setPageStrokes(prev => ({
            ...prev,
            [currentPage]: updatedStrokes
        }));
        
        // Auto-save after 2 seconds
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            savePageStrokes(currentPage, updatedStrokes);
        }, 2000);
    };

    // Saves run one after another: each one replaces all strokes of its page,
    // so two overlapping saves could leave the page with duplicates.
    const savePageStrokes = (pageNum, strokes) => {
        const run = async () => {
            try {
                setSaving(true);
                await documentMarkupApi.saveStrokes(documentId, strokes, pageNum);
            } catch (err) {
                console.error('Save failed:', err);
            } finally {
                setSaving(false);
            }
        };
        saveQueueRef.current = saveQueueRef.current.then(run);
        return saveQueueRef.current;
    };

    const goToPage = async (pageNum) => {
        // Save current page first
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
            await savePageStrokes(currentPage, pageStrokes[currentPage] || []);
        }
        setCurrentPage(pageNum);
    };

    const handleClearPage = async () => {
        if (window.confirm(`Clear all annotations on page ${currentPage}?`)) {
            setPageStrokes(prev => ({
                ...prev,
                [currentPage]: []
            }));
            await savePageStrokes(currentPage, []);
        }
    };

    const handlePrint = () => {
        // Simple print - browser will handle PDF with canvas overlay
        window.print();
    };

    if (loading) {
        return (
            <div className="DocumentMarkupEditor__container">
                <div className="DocumentMarkupEditor__loading">
                    <i className="fas fa-spinner fa-spin"></i> Loading document...
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="DocumentMarkupEditor__container">
                <div className="DocumentMarkupEditor__error">
                    <i className="fas fa-exclamation-circle"></i> {error}
                    <button onClick={() => navigate('/my-notebook/document-markup')}>
                        Back to Documents
                    </button>
                </div>
            </div>
        );
    }

    const currentStrokes = pageStrokes[currentPage] || [];
    // Saving replaces every stroke of the page, so nothing may be drawn before they are loaded
    const strokesLoaded = Boolean(pageStrokes[currentPage]);

    return (
        <div className="DocumentMarkupEditor__container">
            {/* Header */}
            <div className="DocumentMarkupEditor__header">
                <button 
                    className="DocumentMarkupEditor__back-btn"
                    onClick={() => navigate('/my-notebook/document-markup')}
                >
                    <i className="fas fa-arrow-left"></i> Back
                </button>
                
                <h2 className="DocumentMarkupEditor__title">{documentData?.file_name}</h2>
                
                {/* Page navigation */}
                {totalPages > 1 && (
                    <div className="DocumentMarkupEditor__page-nav">
                        <button 
                            className="DocumentMarkupEditor__page-btn"
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <span className="DocumentMarkupEditor__page-info">
                            Page {currentPage} of {totalPages}
                        </span>
                        <button 
                            className="DocumentMarkupEditor__page-btn"
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                )}
                
                {/* Save status */}
                <div className="DocumentMarkupEditor__status">
                    {saving && (
                        <span className="DocumentMarkupEditor__saving">
                            <i className="fas fa-spinner fa-spin"></i> Saving...
                        </span>
                    )}
                    {!saving && currentStrokes.length > 0 && (
                        <span className="DocumentMarkupEditor__saved">
                            <i className="fas fa-check"></i> Saved
                        </span>
                    )}
                </div>
                
                {/* Actions */}
                <button
                    className="DocumentMarkupEditor__print-btn"
                    onClick={() => setShowExport(true)}
                    title="Download the document with your annotations as a PDF"
                >
                    <i className="fas fa-file-pdf"></i> Export PDF
                </button>

                <button
                    className="DocumentMarkupEditor__print-btn"
                    onClick={handlePrint}
                    title="Print PDF with annotations"
                >
                    <i className="fas fa-print"></i> Print
                </button>
                
                <button
                    className="DocumentMarkupEditor__clear-btn"
                    onClick={handleClearPage}
                    disabled={currentStrokes.length === 0}
                >
                    <i className="fas fa-eraser"></i> Clear Page
                </button>
            </div>

            {/* Toolbar */}
            <div className="DocumentMarkupEditor__toolbar">
                <button 
                    className={`tool-btn ${currentTool === 'pen' ? 'active' : ''}`}
                    onClick={() => setCurrentTool('pen')}
                    title="Pen"
                >
                    <i className="fas fa-pen"></i>
                </button>
                
                <button 
                    className={`tool-btn ${currentTool === 'highlighter' ? 'active' : ''}`}
                    onClick={() => setCurrentTool('highlighter')}
                    title="Highlighter"
                >
                    <i className="fas fa-highlighter"></i>
                </button>
                
                <button 
                    className={`tool-btn ${currentTool === 'eraser' ? 'active' : ''}`}
                    onClick={() => setCurrentTool('eraser')}
                    title="Eraser"
                >
                    <i className="fas fa-eraser"></i>
                </button>
                
                <input 
                    type="color" 
                    value={toolSettings.color}
                    onChange={(e) => updateToolSettings({ color: e.target.value })}
                    title="Color"
                    disabled={currentTool === 'eraser'}
                />
                
                <input 
                    type="range" 
                    min="1" 
                    max={currentTool === 'highlighter' ? 40 : 20}
                    value={toolSettings.thickness}
                    onChange={(e) => updateToolSettings({ thickness: parseInt(e.target.value) })}
                    title={currentTool === 'eraser' ? 'Eraser Size' : 'Thickness'}
                />
                
                <span className="toolbar-label">{currentStrokes.length} annotations</span>
            </div>

            {legacyNotice && (
                <div className="DocumentMarkupEditor__notice" role="status">
                    <i className="fas fa-info-circle"></i>
                    <span>
                        This document has annotations from before we fixed how their position is stored.
                        They were saved without a zoom level, so we placed them by estimate.
                        Please check that they still line up on each page.
                    </span>
                    <button onClick={() => setLegacyNotice(false)} aria-label="Dismiss">
                        &times;
                    </button>
                </div>
            )}

            {/* Annotator */}
            <div className="DocumentMarkupEditor__workspace" ref={workspaceRef}>
                {source && (
                    <SimplePdfAnnotator
                        source={source}
                        pageNumber={currentPage}
                        strokes={currentStrokes}
                        onStrokeAdded={handleStrokeAdded}
                        onStrokesChanged={handleStrokesChanged}
                        currentTool={currentTool}
                        toolSettings={toolSettings}
                        disabled={!strokesLoaded}
                    />
                )}
            </div>

            {/* Footer info */}
            <div className="DocumentMarkupEditor__info">
                <span>{currentStrokes.length} annotation{currentStrokes.length !== 1 ? 's' : ''} on this page</span>
                {totalPages > 1 && (
                    <>
                        <span>•</span>
                        <span>Use arrow keys to navigate</span>
                    </>
                )}
                <span>•</span>
                <span>Auto-save enabled</span>
            </div>

            {showExport && documentData && (
                <MarkupExportDialog
                    markup={{ id: documentId, file_name: documentData.file_name, file_type: documentData.file_type }}
                    loaded={{ document: documentData, fileBytes: fileBytesRef.current, knownStrokes: pageStrokes }}
                    onClose={() => setShowExport(false)}
                />
            )}
        </div>
    );
}

export default SimpleDocumentMarkupEditor;
